import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useEffect, useState, Fragment } from "react";
import {
  AutoField,
  FieldLabel,
  usePuck,
  type ComponentData,
  type Field,
} from "@measured/puck";
import { ChevronRight, LayoutTemplate } from "lucide-react";
import {
  DEFAULT_BLOCK_STYLE,
  normalizeBlockStyle,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import { DesignControls, SpacingControls, TextStyleControls } from "@/components/cms-puck/fields/StyleField";
import { TEXT_TREATMENT_BLOCKS } from "@/components/cms-puck/fields/style/constants";
import { RootFieldsPanel } from "@/components/cms-puck/fields/RootFieldsPanel";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  normalizePageAppearance,
  pageThemeCssVars,
  resolvePageAppearanceStyle,
  type PageAppearance,
} from "@/lib/cms-puck/blocks/appearance";
import type { ContainerHeight, LayoutMaxWidth } from "@/lib/cms-puck/layout";
import {
  findPuckNodePath,
  type PuckNodePathSegment,
} from "@/lib/cms-puck/puck-tree";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained";

/**
 * FieldsPanel — override de `overrides.fields` du `<Puck>` : réorganise TOUT le
 * panneau de droite en 3 onglets quand un BLOC est sélectionné :
 *  - « Contenu » : champs natifs du bloc (hors `style` et hors doublons listés
 *    dans `CONTENT_TAB_EXCLUDED_KEYS`).
 *  - « Design »  : `style.design`  (fond, couleur, typo, traitement de texte…).
 *  - « Avancé »  : `style.spacing` + mise en page du wrapper (+ hauteur Conteneur).
 *
 * Audit des doublons inter-onglets (voir aussi `StyleField.tsx`) :
 *
 * | Doublon                          | Onglet conservé | Masqué de        |
 * |----------------------------------|-----------------|------------------|
 * | Marge/padding Conteneur          | Avancé          | Contenu          |
 * | Hauteur Conteneur                | Avancé          | Contenu          |
 * | Fond preset Conteneur            | Design (Fond)   | Contenu          |
 * | Alignement texte (Titre, etc.)   | Design          | Contenu          |
 * | Marge verticale Séparateur       | Avancé          | Contenu          |
 * | Largeur Conteneur (maxWidth)     | Contenu         | Avancé (width)   |
 * | Alignement Bouton/Icône (pos.)   | Contenu         | Design (text)    |
 *
 * Non doublons (conservés) :
 *  - `align` Contenu sur Bouton/Icône/Téléchargement = position dans le parent.
 *  - `align` Avancé « Alignement du bloc » = marges auto du wrapper.
 *  - `gap` Conteneur/Colonnes/Grille = espacement entre enfants flex/grid.
 *  - `tone` Bandeau / `variant` boutons = presets sémantiques du composant.
 *  - `size` Espace / hauteur PdfReader = dimension intrinsèque du composant.
 */
/** Sélecteur d'item Puck (non exporté par le package → typé localement). */
type ItemSelector = { index: number; zone?: string };

type Props = {
  children: React.ReactNode;
  itemSelector?: ItemSelector | null;
};

type Tab = "content" | "design" | "advanced";

const STYLE_KEY = "style";

/**
 * Champs masqués dans l'onglet « Contenu » : doublon avec Avancé/Design.
 * Les props restent en base pour la rétro-compat (rendu inchangé).
 */
const CONTENT_TAB_EXCLUDED_KEYS: Readonly<Record<string, readonly string[]>> = {
  Container: ["padding", "paddingX", "paddingY", "height", "background"],
  Divider: ["spacing"],
  Heading: ["align"],
  RichText: ["align"],
  Quote: ["align"],
  Cta: ["align"],
};

/** Zone racine de Puck (cf. lib/root-droppable-id : `${rootAreaId}:${rootZone}`). */
const ROOT_DROPPABLE_ID = "root:default-zone";

const FIELD_GROUP_CLASS =
  "min-w-0 max-w-full rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm transition-shadow hover:shadow-md [&_label]:text-sm [&_label]:font-medium [&_label]:text-slate-700";

/** Nombre de colonnes actives du bloc Columns (2, 3 ou 4). */
function columnsBlockCount(props: Record<string, unknown>): 2 | 3 | 4 {
  const v = Number(props.columns);
  if (v === 4) return 4;
  if (v === 3) return 3;
  return 2;
}

/** Masque les champs `colNWidth` au-delà du nombre de colonnes choisi. */
function isContentFieldVisible(
  blockType: string,
  key: string,
  props: Record<string, unknown>,
): boolean {
  if (blockType === "Columns") {
    const match = /^col(\d)Width$/.exec(key);
    if (match) {
      return Number(match[1]) <= columnsBlockCount(props);
    }
  }
  return true;
}

export function FieldsPanel({ children: _children, itemSelector }: Props) {
  const cmsNew = useCmsNew();
  const { selectedItem, dispatch, config, appState, getSelectorForId } =
    usePuck();
  const [tab, setTab] = useState<Tab>("content");
  const theme = usePuckTheme();

  // Variables du thème de la page : les pastilles « Couleurs de la page » du
  // sélecteur de couleur les lisent (le canvas est une iframe à part). Posées
  // aussi sur le panneau racine (page sélectionnée).
  const rootProps = appState.data.root?.props as
    | { appearance?: PageAppearance }
    | undefined;
  const pageThemeVars = pageThemeCssVars(
    theme,
    resolvePageAppearanceStyle(
      theme,
      normalizePageAppearance(rootProps?.appearance),
    ),
  );

  const item = selectedItem as ComponentData | null;
  const itemId = item?.props?.id ?? item?.type;

  useEffect(() => {
    setTab("content");
  }, [itemId]);

  if (!item || !itemSelector) {
    return (
      <div className="contents" style={pageThemeVars as React.CSSProperties}>
        <RootFieldsPanel />
      </div>
    );
  }

  const componentConfig = config.components[item.type];
  const fields = (componentConfig?.fields ?? {}) as Record<string, Field>;
  const props = item.props;
  const blockLabel = componentConfig?.label ?? item.type;

  const nodeId = String(props.id ?? "");
  const breadcrumbPath =
    nodeId.length > 0
      ? findPuckNodePath(appState.data, nodeId, (type) => {
          const comp = config.components[type as keyof typeof config.components];
          return typeof comp?.label === "string" ? comp.label : type;
        })
      : null;

  const selectBreadcrumbSegment = (segment: PuckNodePathSegment) => {
    const selector =
      getSelectorForId(segment.id) ??
      ({ index: segment.index, zone: segment.zone } as const);
    dispatch({
      type: "setUi",
      ui: { itemSelector: selector },
      recordHistory: false,
    });
  };

  const setProp = (key: string, value: unknown) => {
    const next: ComponentData = {
      ...item,
      props: { ...props, [key]: value },
    };
    dispatch({
      type: "replace",
      destinationIndex: itemSelector.index,
      destinationZone: itemSelector.zone || ROOT_DROPPABLE_ID,
      data: next,
    });
  };

  const style: BlockStyle = normalizeBlockStyle(
    (props.style as BlockStyle | undefined) ?? DEFAULT_BLOCK_STYLE,
  );
  const setStyle = (value: BlockStyle) => setProp(STYLE_KEY, value);

  const contentKeys = Object.keys(fields).filter((k) => k !== STYLE_KEY);
  const excludedContentKeys = new Set(CONTENT_TAB_EXCLUDED_KEYS[item.type] ?? []);
  const visibleContentKeys = contentKeys.filter(
    (key) =>
      fields[key]?.type !== "slot" &&
      !excludedContentKeys.has(key) &&
      isContentFieldVisible(item.type, key, props as Record<string, unknown>),
  );

  // Blocs de texte : on affiche le style de texte (Typographie + Traitement du
  // texte) directement dans l'onglet Contenu, à côté du contenu éditable — et on
  // le masque dans Design pour éviter le doublon (cf. TextStyleControls).
  const isTextBlock = TEXT_TREATMENT_BLOCKS.has(item.type);

  return (
    <div
      className="flex h-full min-h-0 min-w-0 flex-col overflow-x-hidden"
      style={pageThemeVars as React.CSSProperties}
    >
      <header className="min-w-0 shrink-0 border-b border-slate-200/80 bg-slate-50/60 px-6 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {cmsNew("cms.new.fields.panel.selectedBlock", "Bloc sélectionné")}
        </p>
        <h2 className="mt-1 break-words text-base font-semibold leading-tight text-slate-900">
          {blockLabel}
        </h2>
        {breadcrumbPath && breadcrumbPath.length > 0 ? (
          <nav
            className="mt-2 flex flex-wrap items-center gap-0.5 text-xs text-slate-500"
            aria-label={cmsNew("cms.new.auto.label.chemin_dimbrication", "Chemin d'imbrication")}
          >
            {breadcrumbPath.map((segment, index) => {
              const isLast = index === breadcrumbPath.length - 1;
              return (
                <Fragment key={segment.id}>
                  {index > 0 ? (
                    <ChevronRight
                      className="mx-0.5 h-3 w-3 shrink-0 text-slate-300"
                      aria-hidden
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => selectBreadcrumbSegment(segment)}
                    className={`max-w-full whitespace-normal break-words rounded px-1 py-0.5 text-left transition-colors hover:bg-slate-200/80 hover:text-slate-800 ${
                      isLast ? "font-medium text-slate-700" : ""
                    }`}
                    title={segment.label ?? segment.type}
                  >
                    {segment.label ?? segment.type}
                  </button>
                </Fragment>
              );
            })}
          </nav>
        ) : null}
      </header>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as Tab)}
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <div className="shrink-0 px-6 pt-4">
          <TabsList className="grid h-10 w-full grid-cols-3 bg-slate-100/90">
            <TabsTrigger value="content" className="text-xs sm:text-sm">
              {cmsNew("cms.new.tabs.content", "Contenu")}
            </TabsTrigger>
            <TabsTrigger value="design" className="text-xs sm:text-sm">
              {cmsNew("cms.new.tabs.design", "Design")}
            </TabsTrigger>
            <TabsTrigger value="advanced" className="text-xs sm:text-sm">
              {cmsNew("cms.new.tabs.advanced", "Avancé")}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-6 pt-4">
          <TabsContent value="content" className="mt-0 min-w-0 space-y-4">
            {item.type === "TextImage" ? (
              <div className="rounded-xl border border-slate-200/70 bg-slate-50/80 px-4 py-3 text-xs leading-relaxed text-slate-500">
                {cmsNew("cms.new.fields.panel.textImageHintBefore", "La colonne texte accepte un")}{" "}
                <span className="font-medium text-slate-700">{cmsNew("cms.new.auto.text.conteneur", "Conteneur")}</span>
                {cmsNew(
                  "cms.new.fields.panel.textImageHintAfter",
                  ", des Colonnes, une Grille, ainsi que Titre, Texte, Bouton, etc. Cliquez le conteneur dans la page pour ses réglages (direction, marges, hauteur, fond…), ou glissez-en un depuis « Mise en page ».",
                )}
              </div>
            ) : null}
            {visibleContentKeys.length === 0 ? (
              <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200/80">
                  <LayoutTemplate className="h-5 w-5 text-slate-400" />
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-600">
                    {cmsNew("cms.new.fields.panel.noContentSettings", "Aucun réglage de contenu")}
                  </p>
                  <p className="text-xs leading-relaxed text-slate-400">
                    {cmsNew(
                      "cms.new.fields.panel.noContentSettingsHint",
                      "Ce bloc n'a pas de champs éditables dans le panneau. Les zones imbriquées se modifient directement sur la page.",
                    )}
                  </p>
                </div>
              </div>
            ) : (
              visibleContentKeys.map((key) => {
                const field = fields[key];
                const fieldId = `${String(props.id ?? item.type)}_${field.type}_${key}`;
                return (
                  <div key={key} className={FIELD_GROUP_CLASS}>
                    <FieldLabel label={field.label ?? key} el="div">
                      <div className="mt-2.5">
                        <AutoField
                          field={field}
                          id={fieldId}
                          value={props[key]}
                          onChange={(value) => setProp(key, value)}
                        />
                      </div>
                    </FieldLabel>
                  </div>
                );
              })
            )}

            {/* Style de texte rapproché du contenu pour les blocs de texte
                (Texte, Titre, Bouton…). Masqué dans Design (hideTextStyle). */}
            {isTextBlock ? (
              <TextStyleControls
                value={style}
                onChange={setStyle}
                blockType={item.type}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="design" className="mt-0 min-w-0">
            <DesignControls
              value={style}
              onChange={setStyle}
              blockType={item.type}
              hideTextStyle={isTextBlock}
            />
          </TabsContent>

          <TabsContent value="advanced" className="mt-0 min-w-0">
            <SpacingControls
              value={style}
              onChange={setStyle}
              blockType={item.type}
              containerHeight={
                item.type === "Container"
                  ? (props.height as ContainerHeight | undefined)
                  : undefined
              }
              onContainerHeightChange={
                item.type === "Container"
                  ? (height) => setProp("height", height)
                  : undefined
              }
              containerContentMaxWidth={
                item.type === "Container"
                  ? (props.maxWidth as LayoutMaxWidth | undefined)
                  : undefined
              }
              blockProps={props}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
